/**
 * BBS Grand Slam Coach Cooler — Live Personalization Preview
 * Product: /products/baseball-coach-cooler
 *
 * Behaviour:
 *  • First keystroke auto-slides to the blank cooler (last slide, index 5)
 *    and overlays the monogram initials in real-time.
 *  • Overlay hides if the user swipes to any other slide; reappears on return.
 *  • Clearing the field hides the overlay and resets navigation.
 */
(function () {
  'use strict';

  if (window.location.pathname.indexOf('baseball-coach-cooler') === -1) return;

  /* ── Calibrated config ──────────────────────────────────────────────────── */
  var LEFT_CX_FRAC    = 0.4883;
  var LEFT_CY_FRAC    = 0.6124;
  var LEFT_SIZE_FRAC  = 0.1211;
  var RIGHT_CX_FRAC   = 0.6122;
  var RIGHT_CY_FRAC   = 0.6089;
  var RIGHT_SIZE_FRAC = 0.1211;
  var LETTER_FONT     = '"Courier New", Courier, monospace';
  var LETTER_COLOR    = '#121212';

  /* Blank cooler — slide index 5 (last of 6 images) */
  var CLEAN_SRC = 'https://cdn.shopify.com/s/files/1/0370/6771/8794/files/calibrator-image-clean.png?v=1783110105';
  var LAST_IDX  = 5;

  /* ── State ───────────────────────────────────────────────────────────────── */
  var inputEl = null;
  var flkty   = null;
  var overlay = null;
  var canvas  = null;
  var ctx     = null;
  var showing = false;
  var didNav  = false;

  var bagImg    = new Image();
  var bagLoaded = false;
  bagImg.crossOrigin = 'anonymous';
  bagImg.onload  = function () { bagLoaded = true; repaint(); };
  bagImg.onerror = function () {
    var fb = new Image();
    fb.onload = function () { bagImg = fb; bagLoaded = true; repaint(); };
    fb.src = CLEAN_SRC;
  };
  bagImg.src = CLEAN_SRC;

  /* ── Overlay ────────────────────────────────────────────────────────────── */
  function buildOverlay(gallery) {
    if (overlay) return;
    if (getComputedStyle(gallery).position === 'static') gallery.style.position = 'relative';

    overlay = document.createElement('div');
    overlay.id = 'bbs-coach-cooler-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.cssText = [
      'position:absolute', 'top:0', 'left:0', 'right:0', 'bottom:0',
      'z-index:20', 'opacity:0', 'transition:opacity .22s ease',
      'pointer-events:none', 'overflow:hidden',
    ].join(';');

    canvas = document.createElement('canvas');
    canvas.style.cssText = 'display:block;width:100%;height:100%;';
    overlay.appendChild(canvas);
    gallery.appendChild(overlay);
    ctx = canvas.getContext('2d');
  }

  /* ── Flickity ────────────────────────────────────────────────────────────── */
  function resolveFlickity(root) {
    if (!window.Flickity) return null;
    var candidates = [root].concat(
      Array.prototype.slice.call(root.querySelectorAll('[data-flickity],.flickity-enabled'))
    );
    for (var i = 0; i < candidates.length; i++) {
      var fl = window.Flickity.data(candidates[i]);
      if (fl) return fl;
    }
    return null;
  }

  function goToLast() {
    if (didNav || !flkty) return;
    didNav = true;
    flkty.select(LAST_IDX, false, false);
  }

  /* ── Visibility ──────────────────────────────────────────────────────────── */
  function setVisible(v) {
    if (!overlay) return;
    overlay.style.opacity = v ? '1' : '0';
    showing = v;
  }

  /* ── Render ──────────────────────────────────────────────────────────────── */
  function repaint() {
    if (!canvas || !ctx || !bagLoaded) return;

    var raw = inputEl ? inputEl.value.trim().toUpperCase().replace(/[^A-Z]/g, '') : '';
    var L = raw.charAt(0);
    var R = raw.charAt(1);

    if (!L) {
      ctx.clearRect(0, 0, canvas.width || 1, canvas.height || 1);
      setVisible(false);
      didNav = false;
      return;
    }

    goToLast();

    var onLast = !flkty || (flkty.selectedIndex === LAST_IDX);
    if (!onLast) { setVisible(false); return; }

    var rect = canvas.getBoundingClientRect();
    var dpr  = window.devicePixelRatio || 1;
    var W = Math.round(rect.width  * dpr) || 600;
    var H = Math.round(rect.height * dpr) || 600;
    if (canvas.width !== W || canvas.height !== H) { canvas.width = W; canvas.height = H; }

    ctx.clearRect(0, 0, W, H);

    /* Cover-fill the clean cooler image */
    var iW = bagImg.naturalWidth  || W;
    var iH = bagImg.naturalHeight || H;
    var sc = (iW / iH > W / H) ? H / iH : W / iW;
    var dW = iW * sc, dH = iH * sc;
    var dX = (W - dW) / 2, dY = (H - dH) / 2;
    ctx.drawImage(bagImg, dX, dY, dW, dH);

    var ref = Math.min(dW, dH);
    drawLetter(dX + LEFT_CX_FRAC  * dW, dY + LEFT_CY_FRAC  * dH, L, ref * LEFT_SIZE_FRAC  * 0.70);
    drawLetter(dX + RIGHT_CX_FRAC * dW, dY + RIGHT_CY_FRAC * dH, R, ref * RIGHT_SIZE_FRAC * 0.70);

    setVisible(true);
  }

  function drawLetter(x, y, ch, fs) {
    if (!ch) return;
    ctx.save();
    ctx.font         = fs + 'px ' + LETTER_FONT;
    ctx.fillStyle    = LETTER_COLOR;
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor  = 'rgba(255,255,255,0.08)';
    ctx.shadowBlur   = Math.max(1, fs * 0.03);
    ctx.fillText(ch, x, y);
    ctx.restore();
  }

  /* ── Find input ──────────────────────────────────────────────────────────── */
  function findInput() {
    var selectors = [
      'input[name="properties[Initials]"]',
      'input[name="properties[initials]"]',
      'input[name="properties[Monogram]"]',
      'input[name="properties[monogram]"]',
      'input[name="properties[Personalization]"]',
      'input[name="properties[Letters]"]',
      'input[name="properties[Name]"]',
    ];
    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (el) return el;
    }
    var found = null;
    document.querySelectorAll('label').forEach(function (lbl) {
      if (found) return;
      var t = lbl.textContent.toLowerCase();
      if (t.indexOf('initial') > -1 || t.indexOf('monogram') > -1 || t.indexOf('letter') > -1) {
        found = lbl.control || document.getElementById(lbl.htmlFor);
      }
    });
    return found;
  }

  /* ── Init ────────────────────────────────────────────────────────────────── */
  function init() {
    var gallery = document.querySelector('[data-product-gallery]')
      || document.querySelector('.product_gallery')
      || document.querySelector('.product-gallery')
      || document.querySelector('[class*="product"][class*="gallery"]');
    if (!gallery) return;

    buildOverlay(gallery);

    var fTries = 0;
    var fPoll = setInterval(function () {
      flkty = resolveFlickity(gallery);
      if (flkty) {
        clearInterval(fPoll);
        flkty.on('change', function (idx) {
          if (!inputEl) return;
          var raw = inputEl.value.trim().toUpperCase().replace(/[^A-Z]/g, '');
          if (!raw) return;
          if (idx === LAST_IDX) repaint();
          else setVisible(false);
        });
      } else if (++fTries > 40) clearInterval(fPoll);
    }, 100);

    var hintStyle = document.createElement('style');
    hintStyle.textContent = '#bbs-cooler-hint{display:none}@media(max-width:767px){#bbs-cooler-hint{display:block;font-size:11px;color:#888;margin-top:5px;letter-spacing:.01em}}';
    document.head.appendChild(hintStyle);

    var iTries = 0;
    var iPoll = setInterval(function () {
      inputEl = findInput();
      if (inputEl) {
        clearInterval(iPoll);
        inputEl.addEventListener('input',  repaint);
        inputEl.addEventListener('change', repaint);
        setTimeout(repaint, 150);

        if (!document.getElementById('bbs-cooler-hint')) {
          var hint = document.createElement('p');
          hint.id = 'bbs-cooler-hint';
          hint.textContent = '★ Preview personalization above';
          var insertAfter = inputEl.closest('.bold_option, .product-form__input, .field, [class*="option"]') || inputEl.parentNode;
          insertAfter.parentNode.insertBefore(hint, insertAfter.nextSibling);
        }
      } else if (++iTries > 80) clearInterval(iPoll);
    }, 100);

    window.addEventListener('resize', function () { if (showing) repaint(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(init, 400); });
  } else {
    setTimeout(init, 400);
  }

})();
